import axios from 'axios'
import {
  Account,
  AccountStats,
  AccountsQueryResponse,
  AccountDetailResponse,
  ClientLog,
  LogsQueryResponse,
  FacebookProfile
} from './_models'
import {getAdminToken} from '../../auth/_requests'

const API_URL = process.env.REACT_APP_API_URL

// Create axios instance with credentials for session-based auth
const adminApi = axios.create({
  withCredentials: true
})

// Add request interceptor to include admin token
adminApi.interceptors.request.use((config) => {
  const token = getAdminToken()
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// ============================================
// ACCOUNTS LIST
// ============================================

// Get accounts list with filters
export function getAccounts(params: {
  search?: string
  is_active?: string
  has_marketplace?: string
  trial_status?: string
  page?: number
  limit?: number
  sort?: string
}): Promise<{ data: AccountsQueryResponse }> {
  return adminApi.get(`${API_URL}/admin/accounts`, { params })
}

// Get account stats
export function getAccountStats(): Promise<{ data: AccountStats }> {
  return adminApi.get(`${API_URL}/admin/accounts/stats/summary`)
}

// ============================================
// CREATE ACCOUNT
// ============================================

export interface CreateAccountData {
  first_name?: string
  last_name?: string
  email: string
  phone?: string
  password: string
  company_name?: string
  trial_period_days?: number
  monthly_payment_amount?: number
  marketplace_enabled?: boolean
}

// Create new account
export function createAccount(
  data: CreateAccountData
): Promise<{ data: { success: boolean; account: Account } }> {
  return adminApi.post(`${API_URL}/admin/accounts`, data)
}

// ============================================
// SINGLE ACCOUNT
// ============================================

// Get account details
export function getAccountDetail(accountId: string): Promise<{ data: AccountDetailResponse }> {
  return adminApi.get(`${API_URL}/admin/accounts/${accountId}`)
}

// Update account
export function updateAccount(
  accountId: string,
  data: Partial<Account>
): Promise<{ data: { success: boolean; account: Account } }> {
  return adminApi.put(`${API_URL}/admin/accounts/${accountId}`, data)
}

// ============================================
// TRIAL MANAGEMENT
// ============================================

// Extend/change subscription period
export function extendTrial(
  accountId: string,
  data: { days?: number; new_date?: string; reason: string }
): Promise<{ data: { success: boolean; trial_period_end: string } }> {
  return adminApi.put(`${API_URL}/admin/accounts/${accountId}/trial-extend`, data)
}

// One-off charge on the account's saved card. The reason becomes the invoice
// line the customer reads, and NO subscription time is granted — unlike the
// regular charge on the payments page.
export function chargeCustomAmount(
  accountId: string,
  data: { amount: number; reason: string; invoice_name?: string; installments?: number }
): Promise<{
  data: {
    success: boolean
    error?: string
    amount_charged?: number
    reason?: string
    extended?: boolean
    provider?: string
  }
}> {
  return adminApi.post(`${API_URL}/admin/payments/${accountId}/charge-custom`, data)
}

// ============================================
// MARKETPLACE MANAGEMENT
// ============================================

// Toggle marketplace access
export function toggleMarketplace(
  accountId: string,
  enabled: boolean
): Promise<{ data: { success: boolean; marketplace_enabled: boolean } }> {
  return adminApi.put(`${API_URL}/admin/accounts/${accountId}/marketplace`, { enabled })
}

// Toggle per-client image watermark opt-in
export function toggleWatermark(
  accountId: string,
  enabled: boolean
): Promise<{ data: { success: boolean; watermark_enabled: boolean } }> {
  return adminApi.put(`${API_URL}/admin/accounts/${accountId}/watermark`, { enabled })
}

// Per-account watermark position + size override. Pass { clear: true } to use the
// global appearance. Any omitted field falls back to the global value.
export interface WatermarkOverride {
  anchor?: WatermarkAnchor
  offsetXPercent?: number
  offsetYPercent?: number
  logoSizePercent?: number
  textSizePercent?: number
}
export function setWatermarkPosition(
  accountId: string,
  override: WatermarkOverride | { clear: true }
): Promise<{ data: { success: boolean; watermark_override: any } }> {
  return adminApi.put(`${API_URL}/admin/accounts/${accountId}/watermark-position`, override)
}

// Per-account watermark state + the global config, for the admin override panel/preview.
export function getAccountWatermark(
  accountId: string
): Promise<{ data: { enabled: boolean; override: WatermarkOverride; global: ImageWatermarkSetting } }> {
  return adminApi.get(`${API_URL}/admin/accounts/${accountId}/watermark`)
}

// Toggle group scheduling access
export function toggleGroupScheduling(
  accountId: string,
  enabled: boolean
): Promise<{ data: { success: boolean; group_scheduling_enabled: boolean } }> {
  return adminApi.put(`${API_URL}/admin/accounts/${accountId}/group-scheduling`, { enabled })
}

// Toggle "spread cross-posts across the day" (admin-only). When on, the bot
// defers later-hour copies instead of bursting them all in the morning, so
// posting lands morning/afternoon/evening.
export function toggleXpostSpread(
  accountId: string,
  enabled: boolean
): Promise<{ data: { success: boolean; xpost_spread_across_day: boolean } }> {
  return adminApi.put(`${API_URL}/admin/accounts/${accountId}/xpost-spread`, { enabled })
}

// Toggle "post anonymously while a profile is limited" (admin-only). When on,
// a limited profile skips xpost/seed groups and posts to each group anonymously.
export function toggleAnonPostLimited(
  accountId: string,
  enabled: boolean
): Promise<{ data: { success: boolean; anon_post_when_limited: boolean } }> {
  return adminApi.put(`${API_URL}/admin/accounts/${accountId}/anon-post-limited`, { enabled })
}

// Turn the xpost seed-group feature on/off for a client (admin-only). Off is a
// hard opt-out — nothing creates a group under their FB identity, now or for
// profiles they add later — and it disarms every profile they already have.
export function toggleSeedGroups(
  accountId: string,
  enabled: boolean
): Promise<{ data: { success: boolean; seed_group_disabled: boolean; disarmed: number } }> {
  return adminApi.put(`${API_URL}/admin/accounts/${accountId}/seed-groups`, { enabled })
}

// PayPal subscriptions on/off for one customer (default off).
export function togglePaypal(accountId: string, enabled: boolean): Promise<{ data: { success: boolean; paypal_enabled: boolean } }> {
  return adminApi.put(`${API_URL}/admin/accounts/${accountId}/paypal`, { enabled })
}

export function cancelPaypalSubscription(accountId: string): Promise<{ data: { success: boolean; status: string } }> {
  return adminApi.post(`${API_URL}/admin/accounts/${accountId}/paypal/cancel`)
}

// Feed-mode timeline deletes for a client: posts feed mode published on a
// profile's own timeline and the bot couldn't (yet) delete.
export interface FeedTrashRecordRow {
  _id: string
  post_id: string | null
  post_url: string | null
  profile_name: string
  status: 'pending' | 'failed' | 'unknown_id'
  attempts: number
  last_error: string
  created_at: string
  failed_at: string | null
  retry_count: number
}
export function getFeedTrash(
  accountId: string
): Promise<{ data: { counts: Record<string, number>; records: FeedTrashRecordRow[] } }> {
  return adminApi.get(`${API_URL}/admin/accounts/${accountId}/feed-trash`)
}
export function retryFeedTrash(
  accountId: string,
  ids?: string[]
): Promise<{ data: { success: boolean; requeued: number } }> {
  return adminApi.post(`${API_URL}/admin/accounts/${accountId}/feed-trash/retry`, ids ? { ids } : {})
}

// Show the post editor's "publish to Marketplace too" checkbox to this customer.
export function toggleMarketplaceCheckbox(
  accountId: string,
  enabled: boolean
): Promise<{ data: { success: boolean; marketplace_checkbox_enabled: boolean } }> {
  return adminApi.put(`${API_URL}/admin/accounts/${accountId}/marketplace-checkbox`, { enabled })
}

// Customer API (keys + docs page in the customer's sidebar).
export function toggleApiAccess(
  accountId: string,
  enabled: boolean
): Promise<{ data: { success: boolean; api_access_enabled: boolean } }> {
  return adminApi.put(`${API_URL}/admin/accounts/${accountId}/api-access`, { enabled })
}

// Show this client REAL numbers on /stats (actual publish times, real "today").
export function toggleStatsRealNumbers(
  accountId: string,
  enabled: boolean
): Promise<{ data: { success: boolean; stats_real_numbers: boolean } }> {
  return adminApi.put(`${API_URL}/admin/accounts/${accountId}/stats-real-numbers`, { enabled })
}

// Toggle schedule page access
export function toggleSchedule(
  accountId: string,
  enabled: boolean
): Promise<{ data: { success: boolean; schedule_enabled: boolean } }> {
  return adminApi.put(`${API_URL}/admin/accounts/${accountId}/schedule`, { enabled })
}

// ============================================
// POST FOOTER
// ============================================

// Set promotional text position for account ('start' | 'end' | null = off, mutually exclusive)
export function setPostFooterPosition(
  accountId: string,
  position: 'start' | 'end' | null
): Promise<{ data: { success: boolean; post_footer_enabled: boolean; post_footer_position: 'start' | 'end' | null } }> {
  return adminApi.put(`${API_URL}/admin/accounts/${accountId}/post-footer`, { position })
}

// Admin customer-status label for an account ('new_account' default | 'working' | 'not_relevant')
export type AccountStatus = 'new_account' | 'working' | 'not_relevant'
export function setAccountStatus(
  accountId: string,
  status: AccountStatus
): Promise<{ data: { success: boolean; account_status: AccountStatus } }> {
  return adminApi.put(`${API_URL}/admin/accounts/${accountId}/account-status`, { status })
}

// Legacy alias — keep so existing callers compile; treats true as 'end'
export function togglePostFooter(
  accountId: string,
  enabled: boolean
): Promise<{ data: { success: boolean; post_footer_enabled: boolean; post_footer_position: 'start' | 'end' | null } }> {
  return setPostFooterPosition(accountId, enabled ? 'end' : null)
}

// Get post footer text (global setting)
export function getPostFooterText(): Promise<{ data: { post_footer_text: string } }> {
  return adminApi.get(`${API_URL}/admin/accounts/settings/post-footer`)
}

// Update post footer text (global setting)
export function updatePostFooterText(
  text: string
): Promise<{ data: { success: boolean; post_footer_text: string } }> {
  return adminApi.put(`${API_URL}/admin/accounts/settings/post-footer`, { text })
}

// Get the welcome-on-register WhatsApp message template (global setting).
// Placeholders: {first_name} {email} {password} {login_url}
export function getRegisterWelcome(): Promise<{
  data: { register_welcome_text: string; is_default: boolean }
}> {
  return adminApi.get(`${API_URL}/admin/accounts/settings/register-welcome`)
}

// Update the welcome message template. An empty string resets to the default.
export function updateRegisterWelcome(
  text: string
): Promise<{ data: { success: boolean; register_welcome_text: string; is_default: boolean } }> {
  return adminApi.put(`${API_URL}/admin/accounts/settings/register-welcome`, { text })
}

// ============================================
// TRIAL-EXPIRY REMINDER MESSAGES (authoring only)
// ============================================

export interface TrialReminderEmailDay {
  subject: string
  body: string
}
export interface TrialRemindersConfig {
  enabled: boolean
  email: {
    enabled: boolean
    d3: TrialReminderEmailDay
    d2: TrialReminderEmailDay
    d1: TrialReminderEmailDay
  }
  whatsapp: {enabled: boolean; d3: string; d2: string; d1: string}
  sms: {enabled: boolean; d3: string; d2: string; d1: string}
}

export function getTrialRemindersSetting(): Promise<{data: TrialRemindersConfig}> {
  return adminApi.get(`${API_URL}/admin/accounts/settings/trial-reminders`)
}

export function updateTrialRemindersSetting(
  payload: TrialRemindersConfig
): Promise<{data: TrialRemindersConfig & {success: boolean}}> {
  return adminApi.put(`${API_URL}/admin/accounts/settings/trial-reminders`, payload)
}

// ============================================
// REFERRAL SIDEBAR
// ============================================

// Get referral sidebar setting
export function getReferralSidebarSetting(): Promise<{ data: { enabled: boolean } }> {
  return adminApi.get(`${API_URL}/admin/accounts/settings/referral-sidebar`)
}

// Update referral sidebar setting
export function updateReferralSidebarSetting(
  enabled: boolean
): Promise<{ data: { success: boolean; enabled: boolean } }> {
  return adminApi.put(`${API_URL}/admin/accounts/settings/referral-sidebar`, { enabled })
}

// Referral-promo popup enabled flag (the monthly in-app popup)
export function getReferralPromoSetting(): Promise<{ data: { enabled: boolean } }> {
  return adminApi.get(`${API_URL}/admin/accounts/settings/referral-promo`)
}
export function updateReferralPromoSetting(
  enabled: boolean
): Promise<{ data: { success: boolean; enabled: boolean } }> {
  return adminApi.put(`${API_URL}/admin/accounts/settings/referral-promo`, { enabled })
}

// ============================================
// TUTORIAL SKIP (customer-facing "skip tutorial" button on every step)
// ============================================

export function getPostWaLinkSetting(): Promise<{ data: { enabled: boolean } }> {
  return adminApi.get(`${API_URL}/admin/accounts/settings/post-wa-link`)
}
export function updatePostWaLinkSetting(
  enabled: boolean
): Promise<{ data: { success: boolean; enabled: boolean } }> {
  return adminApi.put(`${API_URL}/admin/accounts/settings/post-wa-link`, { enabled })
}

export function getPostStrengthSetting(): Promise<{ data: { enabled: boolean } }> {
  return adminApi.get(`${API_URL}/admin/accounts/settings/post-strength`)
}
export function updatePostStrengthSetting(
  enabled: boolean
): Promise<{ data: { success: boolean; enabled: boolean } }> {
  return adminApi.put(`${API_URL}/admin/accounts/settings/post-strength`, { enabled })
}

export function getTutorialSkipSetting(): Promise<{ data: { enabled: boolean } }> {
  return adminApi.get(`${API_URL}/admin/accounts/settings/tutorial-skip`)
}
export function updateTutorialSkipSetting(
  enabled: boolean
): Promise<{ data: { success: boolean; enabled: boolean } }> {
  return adminApi.put(`${API_URL}/admin/accounts/settings/tutorial-skip`, { enabled })
}

// ============================================
// LOGGED-IN THRESHOLD (hours window for non-admin display)
// ============================================

export function getLoggedInThresholdSetting(): Promise<{ data: { hours: number; enabled: boolean } }> {
  return adminApi.get(`${API_URL}/admin/accounts/settings/logged-in-threshold`)
}

export function updateLoggedInThresholdSetting(
  payload: { hours?: number; enabled?: boolean }
): Promise<{ data: { success: boolean; hours?: number; enabled?: boolean } }> {
  return adminApi.put(`${API_URL}/admin/accounts/settings/logged-in-threshold`, payload)
}

// ============================================
// FACEBOOK PROFILE LOGIN METHOD (QR vs username/password)
// ============================================
// Off = every login surface (dashboard, onboarding, affiliate, the links we send
// clients) uses username/password. On = the QR scan flow comes back everywhere.

export function getProfileQrLoginSetting(): Promise<{ data: { enabled: boolean } }> {
  return adminApi.get(`${API_URL}/admin/accounts/settings/profile-qr-login`)
}

export function updateProfileQrLoginSetting(
  enabled: boolean
): Promise<{ data: { success: boolean; enabled: boolean } }> {
  return adminApi.put(`${API_URL}/admin/accounts/settings/profile-qr-login`, { enabled })
}

// Live login session on /c/:code. Off = the customer submits their Facebook
// username + password and is told the login has started; on = the streamed
// browser session comes back.

export function getProfileLoginSessionSetting(): Promise<{ data: { enabled: boolean } }> {
  return adminApi.get(`${API_URL}/admin/accounts/settings/profile-login-session`)
}

export function updateProfileLoginSessionSetting(
  enabled: boolean
): Promise<{ data: { success: boolean; enabled: boolean } }> {
  return adminApi.put(`${API_URL}/admin/accounts/settings/profile-login-session`, { enabled })
}

// ============================================
// GLOBAL AUTOMATION KILL SWITCH
// ============================================

export function getAutomationSetting(): Promise<{ data: { enabled: boolean } }> {
  return adminApi.get(`${API_URL}/admin/accounts/settings/automation`)
}

export function updateAutomationSetting(
  enabled: boolean
): Promise<{ data: { success: boolean; enabled: boolean } }> {
  return adminApi.put(`${API_URL}/admin/accounts/settings/automation`, { enabled })
}

// ============================================
// IMAGE WATERMARK (logo + text on posted photos)
// ============================================

export type WatermarkAnchor =
  | 'top-left' | 'top-center' | 'top-right'
  | 'middle-left' | 'middle-center' | 'middle-right'
  | 'bottom-left' | 'bottom-center' | 'bottom-right'

export interface ImageWatermarkSetting {
  enabled: boolean
  imageUrl: string
  text: string
  anchor: WatermarkAnchor
  offsetXPercent: number
  offsetYPercent: number
  logoSizePercent: number
  textSizePercent: number
  opacity: number
  textColor: string
}

export function getImageWatermarkSetting(): Promise<{ data: ImageWatermarkSetting }> {
  return adminApi.get(`${API_URL}/admin/accounts/settings/image-watermark`)
}

export function updateImageWatermarkSetting(
  payload: ImageWatermarkSetting
): Promise<{ data: ImageWatermarkSetting & { success: boolean } }> {
  return adminApi.put(`${API_URL}/admin/accounts/settings/image-watermark`, payload)
}

// ============================================
// LOGIN WHATSAPP SENDER (Baileys connection state)
// ============================================

export interface LoginWhatsappState {
  status: string // 'connected' | 'qr' | 'connecting' | 'logged_out' | 'disconnected' | 'unknown'
  qr: string
  number: string
}

export function getLoginWhatsappState(): Promise<{ data: LoginWhatsappState }> {
  return adminApi.get(`${API_URL}/admin/accounts/settings/login-whatsapp`)
}

// ============================================
// EMAIL VERIFICATION
// ============================================

// Toggle email verification
export function toggleEmailVerified(
  accountId: string,
  verified: boolean
): Promise<{ data: { success: boolean; email_verified: boolean } }> {
  return adminApi.put(`${API_URL}/admin/accounts/${accountId}/email-verified`, { verified })
}

// ============================================
// ACCOUNT ACTIVATION
// ============================================

// Activate/deactivate account
export function toggleAccountActivation(
  accountId: string,
  active: boolean
): Promise<{ data: { success: boolean; is_locked: boolean } }> {
  return adminApi.put(`${API_URL}/admin/accounts/${accountId}/activate`, { active })
}

// ============================================
// FULL ACCOUNT DISABLE
// ============================================

export interface DisableImpact {
  ok: boolean
  is_incubator: boolean
  already_locked: boolean
  profiles: {_id: string; name: string; facebook_id: string}[]
  profiles_count: number
  virtual_numbers: {ddi: string; forward_number: string; enabled: boolean}[]
  virtual_numbers_count: number
  will_reset_key: boolean
}

// What a full disable would do to this account — drives the confirmation dialog.
export function getAccountDisableImpact(accountId: string): Promise<{ data: DisableImpact }> {
  return adminApi.get(`${API_URL}/admin/accounts/${accountId}/disable-preview`)
}

// Release rented profiles + virtual numbers, poison the client key, lock. Not reversible.
export function disableAccount(accountId: string): Promise<{
  data: {
    ok: boolean
    profiles_moved: number
    virtual_numbers_released: string[]
    key_reset: boolean
  }
}> {
  return adminApi.post(`${API_URL}/admin/accounts/${accountId}/disable`, {})
}

// ============================================
// PROFILES MANAGEMENT
// ============================================

// Get profiles for account
export function getAccountProfiles(accountId: string): Promise<{ data: { profiles: FacebookProfile[] } }> {
  return adminApi.get(`${API_URL}/admin/accounts/${accountId}/profiles`)
}

// Toggle profile status
export function toggleProfileStatus(
  accountId: string,
  profileId: string,
  enabled: boolean
): Promise<{ data: { success: boolean; profile: FacebookProfile } }> {
  return adminApi.put(`${API_URL}/admin/accounts/${accountId}/profiles/${profileId}/toggle`, { enabled })
}

// Update posts per day for a profile
export function updateProfilePostsPerDay(
  accountId: string,
  profileId: string,
  postsPerDay: number
): Promise<{ data: { success: boolean; posts_per_day: number } }> {
  return adminApi.put(`${API_URL}/admin/accounts/${accountId}/profiles/${profileId}/posts-per-day`, { posts_per_day: postsPerDay })
}

// ============================================
// LOGS MANAGEMENT
// ============================================

// Get logs for account
export function getAccountLogs(
  accountId: string,
  params?: { page?: number; limit?: number }
): Promise<{ data: LogsQueryResponse }> {
  return adminApi.get(`${API_URL}/admin/accounts/${accountId}/logs`, { params })
}

// Add log comment
export function addAccountLog(
  accountId: string,
  data: {
    subject?: string
    description?: string
    is_pinned?: boolean
    pinned_type?: string
  }
): Promise<{ data: { success: boolean; log: ClientLog } }> {
  return adminApi.post(`${API_URL}/admin/accounts/${accountId}/logs`, data)
}

// Delete log
export function deleteAccountLog(
  accountId: string,
  logId: string
): Promise<{ data: { success: boolean } }> {
  return adminApi.delete(`${API_URL}/admin/accounts/${accountId}/logs/${logId}`)
}

// ============================================
// LOGIN AS USER
// ============================================

// Login as user - get JWT token
export function loginAsUser(
  accountId: string
): Promise<{ data: { success: boolean; api_token: string } }> {
  return adminApi.post(`${API_URL}/admin/accounts/${accountId}/login-as`)
}

// Force-logout a user — invalidates all existing tokens for that account only
export function forceLogoutUser(
  accountId: string
): Promise<{ data: { success: boolean; token_version: number } }> {
  return adminApi.post(`${API_URL}/admin/accounts/${accountId}/force-logout`)
}

// Send the welcome WhatsApp by hand (Google sign-ups never get one automatically).
// Repeatable: the generated password is stored and re-used, so a re-send repeats the
// same working message. newPassword forces a fresh password instead.
export function sendWelcomeMessage(
  accountId: string,
  newPassword?: boolean
): Promise<{ data: { success: boolean; phone: string; password: string; reused: boolean } }> {
  return adminApi.post(`${API_URL}/admin/accounts/${accountId}/send-welcome`, {
    new_password: !!newPassword,
  })
}

// Activate/deactivate affiliate mode for an account
export function toggleAffiliate(
  accountId: string,
  is_affiliate: boolean
): Promise<{ data: { success: boolean; is_affiliate: boolean } }> {
  return adminApi.post(`${API_URL}/admin/accounts/${accountId}/toggle-affiliate`, {is_affiliate})
}

// Support-chat quick questions (shared across the support team)
export function getSupportQuickQuestions(): Promise<{ data: { questions: string[] } }> {
  return adminApi.get(`${API_URL}/admin/accounts/support/quick-questions`)
}
export function saveSupportQuickQuestions(questions: string[]): Promise<{ data: { success: boolean; questions: string[] } }> {
  return adminApi.put(`${API_URL}/admin/accounts/support/quick-questions`, {questions})
}

// AI support assistant — ask questions about this account (answers in Hebrew)
export function supportChat(
  accountId: string,
  messages: Array<{role: string; content: string}>
): Promise<{ data: { reply: string } }> {
  return adminApi.post(`${API_URL}/admin/accounts/${accountId}/support-chat`, {messages})
}

// Enable/disable client bulk-profile management + set the free max
export function setBulkProfiles(
  accountId: string,
  payload: {enabled?: boolean; max?: number}
): Promise<{ data: { success: boolean; bulk_profiles_enabled: boolean; max_self_profiles: number } }> {
  return adminApi.post(`${API_URL}/admin/accounts/${accountId}/bulk-profiles`, payload)
}

// ============================================
// SLOT MANAGEMENT
// ============================================

// Get slots for account
export function getAccountSlots(accountId: string): Promise<{ data: any }> {
  return adminApi.get(`${API_URL}/admin/accounts/${accountId}/slots`)
}

// Add slot to account
export function addAccountSlot(
  accountId: string,
  data: { slot_type: 'profile' | 'subprofile'; count?: number; is_free?: boolean; reason?: string }
): Promise<{ data: any }> {
  return adminApi.post(`${API_URL}/admin/accounts/${accountId}/slots/add`, data)
}

// Remove slot from account
export function removeAccountSlot(
  accountId: string,
  slotId: string,
  reason?: string
): Promise<{ data: any }> {
  return adminApi.delete(`${API_URL}/admin/accounts/${accountId}/slots/${slotId}`, { data: { reason } })
}

// Assign profile to slot
export function assignProfileToSlot(
  accountId: string,
  slotId: string,
  profileId: string
): Promise<{ data: any }> {
  return adminApi.put(`${API_URL}/admin/accounts/${accountId}/slots/${slotId}/assign`, { profile_id: profileId })
}

// Unassign profile from slot
export function unassignProfileFromSlot(
  accountId: string,
  slotId: string
): Promise<{ data: any }> {
  return adminApi.put(`${API_URL}/admin/accounts/${accountId}/slots/${slotId}/unassign`)
}

// Get package timeline for account
export function getPackageTimeline(
  accountId: string,
  params?: { page?: number; limit?: number }
): Promise<{ data: any }> {
  return adminApi.get(`${API_URL}/admin/accounts/${accountId}/package-timeline`, { params })
}

// Get plan payments for account
export function getPlanPayments(
  accountId: string,
  params?: { page?: number; limit?: number }
): Promise<{ data: any }> {
  return adminApi.get(`${API_URL}/admin/accounts/${accountId}/plan-payments`, { params })
}

// ============================================
// CAMPAIGN TRANSFER
// ============================================

export function transferCampaigns(
  accountId: string,
  targetAccountId: string
): Promise<{ data: { success: boolean; transferredCount: number } }> {
  return adminApi.post(`${API_URL}/admin/accounts/${accountId}/transfer-campaigns`, { target_account_id: targetAccountId })
}

export function transferSingleCampaign(
  sourceAccountId: string,
  campaignId: string,
  targetAccountId: string,
  profileIds: string[]
): Promise<{ data: { success: boolean; campaign: string; movedProfileCount: number; movedSlotCount: number } }> {
  return adminApi.post(
    `${API_URL}/admin/accounts/${sourceAccountId}/transfer-campaign/${campaignId}`,
    { target_account_id: targetAccountId, profile_ids: profileIds }
  )
}

// ============================================
// LIBRARY SHARING
// ============================================

export function getLibrarySharing(accountId: string): Promise<{ data: { success: boolean; shared_accounts: any[] } }> {
  return adminApi.get(`${API_URL}/admin/accounts/${accountId}/library-sharing`)
}

export function addLibrarySharing(accountId: string, targetAccountId: string): Promise<{ data: { success: boolean; account: any } }> {
  return adminApi.post(`${API_URL}/admin/accounts/${accountId}/library-sharing/add`, { target_account_id: targetAccountId })
}

export function removeLibrarySharing(accountId: string, targetId: string): Promise<{ data: { success: boolean } }> {
  return adminApi.delete(`${API_URL}/admin/accounts/${accountId}/library-sharing/${targetId}`)
}

export function syncLibrarySharing(accountId: string): Promise<{ data: { success: boolean; synced: number } }> {
  return adminApi.post(`${API_URL}/admin/accounts/${accountId}/library-sharing/sync`)
}

// ============================================
// CHILD ACCOUNTS / BILLING
// ============================================

export function getChildAccounts(accountId: string): Promise<{ data: { success: boolean; child_accounts: any[]; billing_day: number; parent_monthly: number } }> {
  return adminApi.get(`${API_URL}/admin/accounts/${accountId}/child-accounts`)
}

export function addChildAccount(accountId: string, data: { target_account_id: string; monthly_amount?: number; label?: string }): Promise<{ data: { success: boolean; account: any } }> {
  return adminApi.post(`${API_URL}/admin/accounts/${accountId}/child-accounts/add`, data)
}

export function removeChildAccount(accountId: string, childId: string): Promise<{ data: { success: boolean } }> {
  return adminApi.delete(`${API_URL}/admin/accounts/${accountId}/child-accounts/${childId}`)
}

export function updateChildAccount(accountId: string, childId: string, data: { monthly_amount?: number; label?: string }): Promise<{ data: { success: boolean } }> {
  return adminApi.put(`${API_URL}/admin/accounts/${accountId}/child-accounts/${childId}`, data)
}

export function updateBillingDay(accountId: string, billing_day: number): Promise<{ data: { success: boolean } }> {
  return adminApi.put(`${API_URL}/admin/accounts/${accountId}/billing-day`, { billing_day })
}

export function syncChildTrials(accountId: string): Promise<{ data: { success: boolean; synced_count: number; trial_period_end: string } }> {
  return adminApi.post(`${API_URL}/admin/accounts/${accountId}/child-accounts/sync-trial`, {})
}
